import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Flag, Loader2 } from "lucide-react";

const REASONS = [
  { id: "scam", label: "Scam or fraud" },
  { id: "prohibited", label: "Prohibited item (illegal firearms, protected species, etc.)" },
  { id: "spam", label: "Spam or off-topic" },
  { id: "harassment", label: "Harassment or abuse" },
  { id: "misleading", label: "Misleading or inaccurate" },
  { id: "other", label: "Other" },
];

export const ReportDialog = ({ targetType, targetId, testId = "btn-report" }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleTrigger = (e) => {
    if (!user) {
      e.preventDefault();
      navigate("/login");
    }
  };

  const submit = async () => {
    if (!reason) return toast.error("Pick a reason");
    setSubmitting(true);
    try {
      await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        reason,
        details,
      });
      toast.success("Report submitted. Our moderators will take a look.");
      setOpen(false);
      setReason("");
      setDetails("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleTrigger}
          className="text-muted-foreground hover:text-destructive"
          data-testid={testId}
        >
          <Flag className="w-4 h-4 mr-1.5" /> Report
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle>Report this {targetType}</DialogTitle>
          <DialogDescription>
            Help keep the community honest. Reports are anonymous to the reported user.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1.5" data-testid="select-report-reason">
                <SelectValue placeholder="Pick a reason" />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r.id} value={r.id} data-testid={`opt-reason-${r.id}`}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Details (optional)</Label>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Anything the mods should know."
              className="mt-1.5 min-h-[100px]"
              data-testid="input-report-details"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="btn-cancel-report">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || !reason}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="btn-submit-report"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
